import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { CustomInput } from "components/helpers/CustomInput/CustomInput";
import { Button } from "components/ui/Button";
import { Select } from "components/ui/Select/Select";
import { Textarea } from "components/ui/Textarea";
import { useSendSmsForPatient } from "features/comunications/hooks/useSendSmsForPatient";
import { validatePatientForSms } from "features/comunications/utils/validatePatientForSms";
import { useSmsTemplates } from "features/templates/hooks/useSmsTemplates";
import { onInvalidSubmit } from "utils/reportFormErrors";

import s from "./SmsModal2.module.css";

const formSchema = z.object({
  mobilePhone: z.string(),
  smsTemplate: z.string().optional(),
  message: z.string().min(1, { message: "Please enter a message." }),
});

const SmsModal2 = ({ patient, onClose, initialMessage = "" }) => {
  const [patientErrors, setPatientErrors] = useState([]);
  const [sendDisable, setSendDisable] = useState(false);

  const { data: templates = [], isLoading: isLoadingTemplates } =
    useSmsTemplates();
  const sendSmsMutation = useSendSmsForPatient();

  const smsOptions = templates
    .filter((t) => t.value !== "")
    .map((t) => ({ label: t.label, value: t.text || "" }));

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      mobilePhone: patient.mobilePhone || "",
      smsTemplate: "",
      message: initialMessage || "",
    },
  });

  useEffect(() => {
    const errors = validatePatientForSms(patient);

    setPatientErrors(errors);
    setSendDisable(errors.length > 0);
    form.reset({
      mobilePhone: patient.mobilePhone || "",
      smsTemplate: "",
      message: initialMessage || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id, initialMessage]);

  const onSubmit = (values) => {
    if (patientErrors.length > 0) return;

    sendSmsMutation.mutate(
      { patientId: patient.id, message: values.message },
      { onSuccess: () => onClose() }
    );
  };

  const patientName = `${patient.firstName || ""} ${patient.surname || ""}`.trim();

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit("SmsModal2"))} className={s.form}>
        {patientErrors.length > 0 && (
          <div className={s.errorBanner}>
            {patientErrors.map((err, i) => (
              <p key={i} className={s.errorText}>
                {err}
              </p>
            ))}
          </div>
        )}

        <FormField name="recipientName" label="Recipient Name">
          <CustomInput value={patientName} disabled />
        </FormField>

        <FormField name="mobilePhone" label="Mobile Number">
          <CustomInput {...form.register("mobilePhone")} />
        </FormField>

        <FormField name="smsTemplate" label="SMS Template">
          <Controller
            name="smsTemplate"
            control={form.control}
            render={({ field }) => (
              <Select
                options={smsOptions}
                value={field.value}
                onChange={(val) => {
                  field.onChange(val);

                  if (val) {
                    const currentMessage = form.getValues("message") || "";
                    form.setValue(
                      "message",
                      currentMessage ? `${currentMessage}\n${val}` : val
                    );
                  }
                }}
                placeholder={
                  isLoadingTemplates
                    ? "Loading templates..."
                    : "Select a template..."
                }
                disabled={isLoadingTemplates}
                fullWidth
              />
            )}
          />
        </FormField>

        <FormField name="message" label="Message">
          <Textarea
            {...form.register("message")}
            placeholder="Write here..."
            rows={6}
          />
        </FormField>

        <div className={s.footer}>
          <Button
            size="large"
            type="submit"
            disabled={sendDisable || sendSmsMutation.isPending}
          >
            {sendSmsMutation.isPending ? "Sending..." : "Send"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export { SmsModal2 };
